import React, {useState} from 'react';
import {ActivityIndicator, Platform, StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import Animated, {FadeIn} from 'react-native-reanimated';
import Toast from 'react-native-toast-message';
import {observer} from 'mobx-react-lite';

import PaymentMethodComponent from '../../../components/payments/till/PaymentMethod';
import ZenGiftCardsManager from '../../../components/payments/zenGiftCards/ZenGiftCardsManager';
import PickupDeliverComponent from '../../../components/pickupDeliver/PickupDeliverComponent';
import TextInputModal from '../../../components/TextInputModal';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import AddressEntry from '../../../components/ui-controls/address/AddressEntry';
import Button from '../../../components/ui-controls/Button';
import Dialog from '../../../components/ui-controls/Dialog';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Link from '../../../components/ui-controls/Link';
import Text from '../../../components/ui-controls/Text';
import config from '../../../config';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {Address} from '../../../models/mfl';
import {
  InformationContentType,
  RouteNavigatorScreenProps,
} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import {OrderLine} from '../../../transport/apiServiceResponse';
import {Analytics} from '../../../utils/analytics/analytics';
import {
  EventName,
  EventScreenName,
  EventSourceType,
} from '../../../utils/analytics/events';
import CartLineItem from '../components/CartLineItem';
import OrderTotals from '../components/OrderTotals';
import ThreeDsMobileBrowserDataIframe from '../components/ThreeDsMobileBrowserDataIframe';
import WebDesktopSection from '../components/WebDesktopSection';
import useFetchMobileWebView3dsBrowserDataJSFocusEffect from '../useFetchMobileWebView3dsBrowserDataJSFocusEffect';

const CheckoutScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.CART_ORDER_CHECKOUT>) => {
    const {cartStore, paymentStore, userStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();

    const {mobileWebView3DsBrowserDataJS, browserData, setBrowserData} =
      useFetchMobileWebView3dsBrowserDataJSFocusEffect();

    const [isOrderNotesModalVisible, setIsOrderNotesModalVisible] =
      useState(false);
    const [isCouponModalVisible, setIsCouponModalVisible] = useState(false);
    const [isConfirmOrderModalVisible, setIsConfirmOrderModalVisible] =
      useState(false);

    const updateBillingAddress = (value: Address) => {
      const data = {
        firstName: userStore.firstName,
        lastName: userStore.lastName,
        email: userStore.emailAddress,
        dateOfBirth: userStore.dateOfBirth,
        phoneNumber: userStore.phoneNumber,
        billingAddress: {...value},
      };
      userStore.updateUserInfo('CHECKOUT_UPDATE_BILLING_ADDRESS', data);
    };

    const saveOrderNote = (value: any) => {
      cartStore.setOrderNotes(value);
      cartStore.updateOrderNotes();
      setIsOrderNotesModalVisible(false);
    };

    const renderOrderNotes = () => (
      <View style={styles.section} testID="checkout-screen-order-notes">
        <Text style={styles.label}>Order notes</Text>
        <TextInputModal
          isVisible={isOrderNotesModalVisible}
          title="Add note to store"
          saveAction={saveOrderNote}
          saveText="UPDATE STORE NOTE"
          closeAction={() => setIsOrderNotesModalVisible(false)}
          openAction={() => setIsOrderNotesModalVisible(true)}
          initialValue={cartStore.orderNotes}
          placeholder="Add note to store"
        />
      </View>
    );

    const saveCoupon = (value: any) => {
      cartStore.updateCoupon(value);
      setIsCouponModalVisible(false);
    };

    const renderCouponInput = () => (
      <View style={styles.section} testID="checkout-screen-coupon-input">
        <Text style={styles.label}>Coupon</Text>
        <TextInputModal
          isVisible={isCouponModalVisible}
          title="Add coupon to order"
          saveAction={saveCoupon}
          saveText="SAVE COUPON"
          closeAction={() => setIsCouponModalVisible(false)}
          openAction={() => setIsCouponModalVisible(true)}
          initialValue={cartStore.currentOrder?.coupon_code}
          placeholder="Add coupon code"
          allCaps
        />
      </View>
    );

    const renderLineItem = ({
      item,
      index,
    }: {
      item: OrderLine;
      index: number;
    }) => (
      <View
        style={styles.cartItemSpacing}
        key={`${index}-${item.id}`}
        testID="checkout-screen-cart-line-item-wrapper">
        <CartLineItem
          item={item}
          index={index}
          disabled={cartStore.submittingOrder}
        />
      </View>
    );

    const renderOrderItems = () => (
      <View style={styles.section} testID="checkout-screen-your-order">
        {!isWebDesktopScreen && <Text style={styles.label}>Your Order</Text>}
        {cartStore.currentOrder?.order_lines
          ?.filter((line: OrderLine) => !line.charge)
          .map((item: OrderLine, index: number) =>
            renderLineItem({item, index}),
          )}
      </View>
    );

    const renderPaymentMethod = () => (
      <View style={styles.section}>
        <Text style={styles.label}>Payment method</Text>
        <Animated.View
          style={{
            padding: 4,
            borderColor: 'grey',
            borderWidth: 0.4,
            borderRadius: 4,
          }}
          entering={FadeIn}>
          <PaymentMethodComponent
            navigationTarget={AppRoute.CART_ORDER_CHECKOUT}
          />
        </Animated.View>
      </View>
    );

    const renderBillingAddress = () => (
      <View style={styles.section} testID="checkout-screen-billing-address">
        <Text style={styles.label}>Billing address</Text>
        <AddressEntry
          address={userStore.billingAddress}
          onChange={updateBillingAddress}
          placeholder="Billing address"
        />
      </View>
    );

    const getPlaceOrderValidationError = () => {
      if (!cartStore.currentOrder?.order_lines) {
        return 'Cannot place an empty order';
      }

      if (!cartStore.selectedSlot) {
        return 'To place your order please select a delivery or pick up time.';
      }

      if (
        cartStore.hasNominatedPerson &&
        !cartStore.nominatedPersonName.length
      ) {
        return 'A person (18+) must be nominated to accept the delivery';
      }

      // Payment methods and gift cards
      const isGiftCardsBeingUsed = cartStore.isGiftCardsBeingUsed();
      const isGiftCardsNotCoveringTotal =
        cartStore.getTotalChargedOnGiftCardsCents() < cartStore.totalAmount;
      const isCreditCardAvailable = paymentStore.tillUserPaymentInstrument;
      if (
        isGiftCardsBeingUsed &&
        isGiftCardsNotCoveringTotal &&
        !isCreditCardAvailable
      ) {
        return "The gift cards do not cover the total amount and there's no other payment method available. Please add a debit/credit card";
      }

      if (!isGiftCardsBeingUsed && !paymentStore.tillUserPaymentInstrument) {
        return 'Cannot place an order without a valid payment method';
      }

      return null;
    };

    const handlePlaceOrder = () => {
      const error = getPlaceOrderValidationError();

      if (error) {
        console.log('☠️', error);
        Toast.show({
          type: ERROR_MESSAGE,
          text1: error,
        });
      } else {
        setIsConfirmOrderModalVisible(true);
      }
    };

    const submitOrder = () => {
      setIsConfirmOrderModalVisible(false);
      cartStore.checkoutOrder(navigation, browserData);
      Analytics.logEvent({
        eventName: EventName.SubmitOrder,
        parameters: {
          screen_name: EventScreenName.Checkout,
          source_type: EventSourceType.Button,
          value: cartStore.currentOrder?.total?.cents_inc_tax / 100,
          tax: cartStore.currentOrder?.total?.cents_tax / 100,
          currency: 'AUD',
        },
      });
    };

    const ConfirmOrderPlacementModal = () => (
      <Dialog
        isVisible={isConfirmOrderModalVisible}
        title="Place Order"
        okText="CONFIRM & SUBMIT"
        cancelText="BACK"
        onCancel={() => setIsConfirmOrderModalVisible(false)}
        onOk={submitOrder}>
        <View>
          {!!cartStore.isDelivery && (
            <>
              <Text style={styles.confirmText}>
                The registered customer or other adult nominated to accept the
                delivery must present a valid form of ID to the delivery person,
                which will be securely scanned through the delivery
                person&apos;s app to verify that you are aged 18 or over.
              </Text>
              <Text style={styles.confirmText}>
                Delivery will be refused if ID is not shown.
              </Text>
              <Text style={styles.confirmText}>
                By placing this order, you confirm that you are over 18 years
                old and that you agree to our{' '}
                <Link
                  callback={() => setIsConfirmOrderModalVisible(false)}
                  target={AppRoute.INFORMATION_CONTENT}
                  navigationParams={{
                    target: InformationContentType.TERMS_AND_CONDITIONS,
                  }}>
                  Terms and Conditions
                </Link>
                .
              </Text>
            </>
          )}
          {!!cartStore.isPickup && (
            <>
              <Text style={styles.confirmText}>
                The registered customer (18+) who placed the order must have a
                valid form ID and the credit card used to pay for this order
                available to show at pick up.
              </Text>
              <Text style={styles.confirmText}>
                Pick up may be refused if any of these items are not shown.
              </Text>
            </>
          )}
        </View>
      </Dialog>
    );

    const renderPlaceOrderButton = () => (
      <Button
        title="PLACE ORDER"
        onPress={handlePlaceOrder}
        disabled={cartStore.submittingOrder}
        loading={cartStore.submittingOrder}
        buttonStyle={styles.bfButton}
        testID="checkout-screen-place-order-button"
      />
    );

    const isGiftCardsBeingUsed = cartStore.isGiftCardsBeingUsed();
    const renderOrderOptions = () => (
      <>
        <PickupDeliverComponent />
        {renderOrderNotes()}
        {renderCouponInput()}
        {renderPaymentMethod()}
        {!!config.FF_IS_GIFT_CARDS_ENABLED && <ZenGiftCardsManager />}
        {!!config.FF_IS_GIFT_CARDS_ENABLED && !!isGiftCardsBeingUsed && (
          <Text style={{marginTop: 8, fontSize: theme.size.text.body2}}>
            The balance(s) above will be used first and any remaining balance
            will be charged on the credit card.
          </Text>
        )}
        {renderBillingAddress()}
      </>
    );

    const renderTotalsAndPlaceOrderButton = () => (
      <>
        <OrderTotals />
        {renderPlaceOrderButton()}
      </>
    );

    if (Platform.OS !== 'web' && !mobileWebView3DsBrowserDataJS) {
      return (
        <ContentView testID="checkout-screen">
          <ActivityIndicator />
        </ContentView>
      );
    }

    return (
      <ContentView testID="checkout-screen">
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always"
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}>
          {isWebDesktopScreen ? (
            <View
              testID="checkout-screen-horizontal-content"
              style={styles.webDesktopHorizontalContent}>
              <WebDesktopSection title="CART">
                {renderOrderItems()}
              </WebDesktopSection>
              <WebDesktopSection title="DELIVERY OR PICK UP">
                {renderOrderOptions()}
              </WebDesktopSection>
              <WebDesktopSection title="PAYMENT">
                {renderTotalsAndPlaceOrderButton()}
              </WebDesktopSection>
            </View>
          ) : (
            <>
              {renderOrderOptions()}
              {renderOrderItems()}
              {renderTotalsAndPlaceOrderButton()}
            </>
          )}
        </KeyboardAwareScrollView>
        <ConfirmOrderPlacementModal />
        <ThreeDsMobileBrowserDataIframe
          mobileWebView3DsBrowserDataJS={mobileWebView3DsBrowserDataJS}
          onBrowserData={setBrowserData}
        />
      </ContentView>
    );
  },
);

export default CheckoutScreen;

const styles = StyleSheet.create({
  webDesktopHorizontalContent: {
    flexDirection: 'row',
    gap: 16,
  },
  webDesktopSectionTitle: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 22,
    backgroundColor: 'white',
  },
  totalText: {
    textAlign: 'right',
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
  },
  totalName: {
    flex: 3,
  },
  totalAmount: {
    flex: 1,
  },
  activityHolder: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  cartItemSpacing: {
    marginBottom: 8,
  },
  bfButton: {
    marginVertical: 16,
  },
  label: {
    color: 'grey',
    fontSize: 12,
    marginBottom: 4,
  },
  section: {
    marginTop: 8,
  },
  confirmText: {
    marginVertical: 8,
    color: theme.colour.text.content.primary,
  },
});
